import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PublicHeader from '@/components/layout/PublicHeader';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { useBenchmarkCTA } from '@/hooks/useBenchmarkCTA';
import {
  Brain, FileText, Users, Mic, Network, BarChart3,
  ArrowRight, Check, Zap, Shield, Globe, Video, TrendingDown,
  Scissors, MessageCircle, BookOpen, UserCog, Sparkles,
  Play, Clock, Star, ChevronRight, Mail, Phone, Calendar,
  CreditCard, Headphones, Server, Lock, Target, Layers
} from 'lucide-react';

const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookingForm, setBookingForm] = useState({ name: '', email: '', company: '', size: '', useCase: '' });
  const [showBooking, setShowBooking] = useState(false);
  const heroCTA = useBenchmarkCTA('landing_hero');

  const features = [
    { icon: Brain, title: 'Knowledge Engine', desc: 'Temporal notes with AI-detected relationships, decision advisor, and closed feedback loops', color: '#002FA7' },
    { icon: Mic, title: 'Voice Journaling', desc: 'Capture thoughts hands-free with Whisper AI transcription', color: '#34C759' },
    { icon: Video, title: 'Webinar Studio', desc: 'Live video conferencing with Jitsi Meet for 100+ attendees', color: '#FF2D55' },
    { icon: Scissors, title: 'Recording Intelligence', desc: 'Auto-transcription, AI chapters, annotations, and shareable clips', color: '#FF9500' },
    { icon: Network, title: 'Knowledge Mapping', desc: 'Interactive dependency graphs revealing hidden connections', color: '#00C7BE' },
    { icon: TrendingDown, title: 'Degradation Tracking', desc: 'Monitor asset freshness and prevent knowledge rot', color: '#AF52DE' },
    { icon: Shield, title: 'Knowledge Audits', desc: 'Proactive gap identification with scheduled health alerts', color: '#5856D6' },
    { icon: UserCog, title: 'HR Workflows', desc: 'Automated onboarding/offboarding knowledge capture', color: '#FF3B30' },
    { icon: Users, title: 'Expertise Locator', desc: 'Find the right expert for any challenge instantly', color: '#007AFF' },
    { icon: FileText, title: 'Article Management', desc: 'Organize and version-control knowledge hierarchically', color: '#4B5563' },
    { icon: Sparkles, title: 'Gap Predictions', desc: 'AI-powered identification of missing knowledge areas', color: '#FF9500' },
    { icon: Target, title: 'Decision Intelligence', desc: 'Structured decision records with outcome tracking and audit', color: '#34C759' },
  ];

  const pricingTiers = [
    {
      name: 'Free', price: '$0', period: 'forever', users: 'Up to 5 users',
      features: ['Basic Dashboard', '50 MB storage', 'Manual article entry', 'Community support'],
      cta: 'Get Started Free', ctaStyle: 'outline', highlight: false,
    },
    {
      name: 'Professional', price: '$12', annualPrice: '$10', period: '/user/mo', users: '6 - 50 users',
      features: ['Full modules (Tasks, Calendar, Inbox)', '10 GB storage', 'Expertise Locator', 'Email support', 'Voice Journaling'],
      cta: 'Start 14-Day Trial', ctaStyle: 'primary', highlight: true, badge: 'Most Popular',
    },
    {
      name: 'Business', price: '$20', annualPrice: '$16', period: '/user/mo', users: '51 - 250 users',
      features: ['Everything in Professional', 'Capability Metrics', 'Knowledge Transfer & Reporting', '50 GB storage', 'Priority support'],
      cta: 'Start 14-Day Trial', ctaStyle: 'primary', highlight: false,
    },
    {
      name: 'Enterprise', price: 'Custom', period: '', users: '251+ users',
      features: ['Everything in Business', 'PaaS API access', 'Dedicated instance', 'SSO/SAML', 'Custom AI training', '500+ GB storage', '24/7 phone support'],
      cta: 'Contact Sales', ctaStyle: 'outline', highlight: false,
    },
  ];

  const trialFeatures = [
    { label: 'Full Business Tier access', included: true },
    { label: '5 user licenses (expandable to 10)', included: true },
    { label: '25 GB storage', included: true },
    { label: '1,000 Voice API calls', included: true },
    { label: 'Capability Metrics & Reporting', included: true },
    { label: 'Guided onboarding checklist', included: true },
    { label: 'No credit card required', included: true },
    { label: 'SSO or custom roles', included: false },
    { label: 'Data export (available after conversion)', included: false },
  ];

  const journeySteps = [
    { step: '1', title: 'Sign Up', desc: 'Create your account in 3 minutes. No credit card needed.', time: '3 min', icon: Mail },
    { step: '2', title: 'Explore', desc: 'Access sandbox with sample data. Follow the guided onboarding tour.', time: 'Days 1-5', icon: Play },
    { step: '3', title: 'Realize Value', desc: 'Upload your first article, create tasks, or test the Voice API.', time: 'Days 3-7', icon: Zap },
    { step: '4', title: 'Convert', desc: 'Keep your data and upgrade seamlessly. Invoice within 1 hour.', time: 'Days 12-14', icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <PublicHeader />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#002FA7]/5 via-transparent to-[#FF9500]/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#002FA7]/10 text-[#002FA7] text-xs font-medium rounded-full mb-6">
              <Zap className="w-3 h-3" />AI-Enhanced Knowledge Management Platform
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A0A0A] tracking-tight leading-tight">
              Your Knowledge,<br />
              <span className="text-[#002FA7]">Actively Cultivated</span>
            </h1>
            <p className="text-base sm:text-lg text-[#4B5563] mt-6 max-w-xl leading-relaxed">
              Transform passive information storage into active knowledge cultivation.
              AI transcription, live webinars, decision intelligence, and 30+ integrated modules — starting free.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/register">
                <Button size="lg" className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="hero-cta-btn">
                  Start 14-Day Free Trial <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="#demo">
                <Button size="lg" variant="outline" data-testid="demo-btn">
                  <Play className="w-4 h-4 mr-2" />Watch Demo
                </Button>
              </a>
              <Link to="/why-kstreak">
                <Button size="lg" variant="ghost" className="text-[#002FA7]" data-testid="why-kstreak-btn">
                  Why KStreak? <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to={heroCTA.href} onClick={heroCTA.onClick}>
                <Button size="lg" variant="outline" className="border-[#FF9500] text-[#FF9500] hover:bg-[#FF9500] hover:text-white" data-testid="benchmark-my-org-btn" data-variant={heroCTA.variant}>
                  <Target className="w-4 h-4 mr-2" />{heroCTA.label}
                </Button>
              </Link>
            </div>
            <p className="text-xs text-[#9CA3AF] mt-3">No credit card required. Full Business Tier for 14 days.</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-2xl">
            {[
              { value: '30+', label: 'Integrated Modules' },
              { value: 'GPT-5.2', label: 'AI Engine' },
              { value: '100+', label: 'Webinar Capacity' },
              { value: '99.99%', label: 'Enterprise SLA' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <p className="text-2xl font-bold text-[#002FA7]">{s.value}</p>
                <p className="text-xs text-[#9CA3AF] mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deployment Models */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#0A0A0A] mb-2">Flexible Deployment for Every Scale</h2>
            <p className="text-sm text-[#4B5563]">SaaS for teams, Enterprise for organizations, PaaS for builders</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: Globe, title: 'SaaS Standard', desc: 'Fully managed multi-tenant cloud. Automatic updates and security patches. Perfect for SMBs and teams.', badge: 'Most Popular', color: '#002FA7' },
              { icon: Server, title: 'SaaS Enterprise', desc: 'Dedicated single-tenant instance. 99.99% SLA, SSO integration, advanced analytics, and priority support.', badge: '500+ employees', color: '#FF9500' },
              { icon: Layers, title: 'PaaS Add-On', desc: 'API-first access to the KStreak kernel. Build custom modules, embed components, or extend the Voice API.', badge: 'For ISVs', color: '#AF52DE' },
            ].map((m, i) => {
              const Icon = m.icon;
              return (
                <Card key={i} className="border-[#E5E7EB] hover:shadow-md transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: m.color + '12' }}>
                        <Icon className="w-5 h-5" style={{ color: m.color }} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#0A0A0A]">{m.title}</h3>
                        <Badge variant="secondary" className="text-[9px]">{m.badge}</Badge>
                      </div>
                    </div>
                    <p className="text-sm text-[#4B5563] leading-relaxed">{m.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#0A0A0A] mb-3">30+ Modules. One Platform.</h2>
            <p className="text-sm text-[#4B5563] max-w-lg mx-auto">Every tool you need for personal and organizational knowledge management — powered by AI at every layer.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="p-5 bg-white border border-[#E5E7EB] rounded-xl hover:shadow-md hover:border-[#002FA7]/20 transition-all group" data-testid={`feature-card-${i}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: f.color + '12' }}>
                    <Icon className="w-5 h-5" style={{ color: f.color }} />
                  </div>
                  <h3 className="text-sm font-semibold text-[#0A0A0A] mb-1 group-hover:text-[#002FA7] transition-colors">{f.title}</h3>
                  <p className="text-xs text-[#4B5563] leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-[#F9FAFB]" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#0A0A0A] mb-2">Simple, Transparent Pricing</h2>
            <p className="text-sm text-[#4B5563]">15% discount for annual prepayment. 10% off for non-profits and education.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {pricingTiers.map((tier, i) => (
              <Card key={i} className={`border-[#E5E7EB] relative ${tier.highlight ? 'border-[#002FA7] shadow-lg ring-2 ring-[#002FA7]/20' : ''}`} data-testid={`pricing-${tier.name.toLowerCase()}`}>
                {tier.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#002FA7] text-white text-[10px] px-3">{tier.badge}</Badge>
                  </div>
                )}
                <CardContent className="p-6 flex flex-col h-full">
                  <h3 className="text-lg font-semibold text-[#0A0A0A]">{tier.name}</h3>
                  <p className="text-xs text-[#9CA3AF] mb-4">{tier.users}</p>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-[#0A0A0A]">{tier.price}</span>
                    {tier.period && <span className="text-sm text-[#9CA3AF] ml-1">{tier.period}</span>}
                    {tier.annualPrice && (
                      <p className="text-xs text-[#34C759] mt-0.5">{tier.annualPrice}/user/mo billed annually</p>
                    )}
                  </div>
                  <div className="space-y-2 flex-1 mb-6">
                    {tier.features.map((f, j) => (
                      <div key={j} className="flex items-start gap-2 text-xs text-[#4B5563]">
                        <Check className="w-3.5 h-3.5 text-[#34C759] mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                  <Link to={tier.name === 'Enterprise' ? '#' : '/register'} onClick={(e) => { if (tier.name === 'Enterprise') { e.preventDefault(); setShowBooking(true); } }}>
                    <Button className={`w-full ${tier.ctaStyle === 'primary' ? 'bg-[#002FA7] hover:bg-[#00227A] text-white' : ''}`} variant={tier.ctaStyle === 'outline' ? 'outline' : 'default'}>
                      {tier.cta}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 14-Day Trial Section */}
      <section id="trial" className="py-20" data-testid="trial-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-[#34C759]/10 text-[#34C759] mb-4">No Credit Card Required</Badge>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#0A0A0A] mb-4">14-Day Free Trial</h2>
              <p className="text-sm text-[#4B5563] mb-6 leading-relaxed">
                Get full access to Business Tier features for 14 days. Upload your own content, test the Voice API,
                and see how KStreak transforms your team's knowledge management. Activate in 3 clicks.
              </p>
              <div className="space-y-2 mb-8">
                {trialFeatures.map((f, i) => (
                  <div key={i} className={`flex items-center gap-2 text-sm ${f.included ? 'text-[#0A0A0A]' : 'text-[#9CA3AF] line-through'}`}>
                    {f.included ? <Check className="w-4 h-4 text-[#34C759] flex-shrink-0" /> : <span className="w-4 h-4 flex-shrink-0 text-center text-xs">-</span>}
                    <span>{f.label}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/register">
                  <Button size="lg" className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="trial-cta-btn">
                    Start 14-Day Free Trial <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Button size="lg" variant="outline" onClick={() => setShowBooking(true)} data-testid="enterprise-trial-btn">
                  Enterprise Trial (50+ users)
                </Button>
              </div>
            </div>
            <div className="bg-[#F9FAFB] rounded-2xl p-8 border border-[#E5E7EB]">
              <h3 className="font-semibold text-[#0A0A0A] mb-4">How to Activate</h3>
              <div className="space-y-4">
                {[
                  { step: '1', title: 'Navigate & Click', desc: 'Click "Start 14-Day Free Trial" above' },
                  { step: '2', title: 'Enter Details', desc: 'Work email, company name, and password (or sign in with Google)' },
                  { step: '3', title: 'Verify Email', desc: 'Click the confirmation link sent to your inbox' },
                  { step: '4', title: 'Instant Access', desc: 'Your Business Tier sandbox is ready immediately' },
                ].map((s, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-7 h-7 bg-[#002FA7] rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">{s.step}</div>
                    <div>
                      <p className="text-sm font-medium text-[#0A0A0A]">{s.title}</p>
                      <p className="text-xs text-[#4B5563]">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo" className="py-20 bg-[#F9FAFB]" data-testid="demo-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#0A0A0A] mb-2">See It in Action</h2>
            <p className="text-sm text-[#4B5563]">Choose your preferred way to experience KStreak</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Self-Guided Demo */}
            <Card className="border-[#E5E7EB] hover:shadow-md transition-all" data-testid="self-guided-demo">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-[#002FA7]/10 rounded-lg flex items-center justify-center">
                    <Play className="w-5 h-5 text-[#002FA7]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A0A0A]">Self-Guided Demo</h3>
                    <p className="text-xs text-[#9CA3AF]">Available 24/7 — No registration</p>
                  </div>
                </div>
                <p className="text-sm text-[#4B5563] mb-4 leading-relaxed">
                  Explore a fully interactive sandbox pre-populated with 50 employee profiles, 200 articles,
                  5 active tasks, and 3 Knowledge Maps. Includes a 5-minute guided tour.
                </p>
                <div className="space-y-1.5 mb-4">
                  {['50 mock employee profiles', '200 knowledge articles (IT, HR, Sales)', '5-minute guided tour overlay', 'Voice demo (microphone optional)'].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-[#4B5563]">
                      <Check className="w-3 h-3 text-[#34C759]" /><span>{f}</span>
                    </div>
                  ))}
                </div>
                <Link to="/demo">
                  <Button className="w-full bg-[#002FA7] hover:bg-[#00227A]" data-testid="self-demo-cta">
                    <Play className="w-4 h-4 mr-2" />Launch Demo
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Live Demo */}
            <Card className="border-[#E5E7EB] hover:shadow-md transition-all" data-testid="live-demo">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-[#FF9500]/10 rounded-lg flex items-center justify-center">
                    <Headphones className="w-5 h-5 text-[#FF9500]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A0A0A]">Live Demo with Sales Engineer</h3>
                    <p className="text-xs text-[#9CA3AF]">30 or 60 minutes — M-F, 9 AM - 5 PM ET/PT</p>
                  </div>
                </div>
                <p className="text-sm text-[#4B5563] mb-4 leading-relaxed">
                  Get a personalized walkthrough with your company logo and industry-relevant data.
                  Post-demo, your tenant stays active for a 7-day extended trial.
                </p>
                <div className="space-y-1.5 mb-4">
                  {['Personalized tenant with your branding', '3 live scenarios tailored to your use case', 'Voice API live test during session', '7-day extended trial after demo', 'Recorded session link sent post-demo'].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-[#4B5563]">
                      <Check className="w-3 h-3 text-[#FF9500]" /><span>{f}</span>
                    </div>
                  ))}
                </div>
                <Button className="w-full" variant="outline" onClick={() => setShowBooking(true)} data-testid="book-demo-cta">
                  <Calendar className="w-4 h-4 mr-2" />Book a Demo
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Customer Journey */}
      <section className="py-20" data-testid="journey-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#0A0A0A] mb-2">Trial to Paid in 4 Steps</h2>
            <p className="text-sm text-[#4B5563]">Most teams realize value within the first week</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {journeySteps.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="relative" data-testid={`journey-step-${i}`}>
                  <div className="flex flex-col items-center text-center">
                    <div className="w-14 h-14 bg-[#002FA7] rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-[#002FA7]/20">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <Badge variant="secondary" className="text-[10px] mb-2">{s.time}</Badge>
                    <h4 className="font-semibold text-[#0A0A0A] mb-1">{s.title}</h4>
                    <p className="text-xs text-[#4B5563] leading-relaxed">{s.desc}</p>
                  </div>
                  {i < journeySteps.length - 1 && (
                    <div className="hidden md:block absolute top-7 -right-3 w-6">
                      <ChevronRight className="w-5 h-5 text-[#D1D5DB]" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits / Why KStreak */}
      <section className="py-20 bg-[#F9FAFB]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#0A0A0A] mb-6">Why Teams Choose KStreak</h2>
              <div className="space-y-3">
                {[
                  'AI-powered weekly digests reduce information synthesis time by 30%',
                  'Live webinars with auto-transcription and chapter markers',
                  'Collaborative annotations create shared knowledge on recordings',
                  'Knowledge clips turn long meetings into shareable micro-content',
                  'Proactive degradation tracking prevents knowledge rot',
                  'Decision intelligence with closed feedback loops improves judgment over time',
                  'Automated HR workflows capture departing employee knowledge',
                ].map((b, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#34C759] rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm text-[#4B5563]">{b}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: Shield, title: 'Enterprise RBAC', desc: 'Role-based access with SSO/SAML', color: '#002FA7' },
                { icon: Lock, title: 'Security First', desc: 'Consent banners, monitoring, compliance', color: '#FF3B30' },
                { icon: BookOpen, title: 'Knowledge Health', desc: 'Live health score dashboard (0-100)', color: '#FF9500' },
                { icon: MessageCircle, title: 'Real-time Collab', desc: 'Annotations, reactions, and threads', color: '#FF2D55' },
              ].map((c, i) => {
                const Icon = c.icon;
                return (
                  <div key={i} className="p-4 bg-white border border-[#E5E7EB] rounded-xl">
                    <Icon className="w-6 h-6 mb-2" style={{ color: c.color }} />
                    <h4 className="text-sm font-semibold mb-0.5">{c.title}</h4>
                    <p className="text-xs text-[#4B5563]">{c.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#002FA7]" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-3">Ready to Cultivate Your Knowledge?</h2>
          <p className="text-sm text-[#93A4D1] mb-8">Start your 14-day free trial today. No credit card required. Full Business Tier access.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-white text-[#002FA7] hover:bg-[#F3F4F6]" data-testid="final-cta-btn">
                Start Free Trial <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" onClick={() => setShowBooking(true)}>
              <Calendar className="w-4 h-4 mr-2" />Book a Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <img src="/logo.png" alt="KStreak" className="w-6 h-6 rounded object-contain" />
                <span className="font-semibold text-white text-sm">KStreak</span>
              </div>
              <p className="text-xs text-[#9CA3AF]">AI-Enhanced Knowledge Management for individuals and organizations.</p>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-white mb-2">Product</h4>
              <div className="space-y-1">
                {['Features', 'Pricing', 'Free Trial', 'Enterprise'].map(l => <a key={l} href={`#${l.toLowerCase().replace(' ','-')}`} className="block text-xs text-[#9CA3AF] hover:text-white transition-colors">{l}</a>)}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-white mb-2">Resources</h4>
              <div className="space-y-1">
                {['Documentation', 'API Reference', 'Blog', 'Webinars'].map(l => <span key={l} className="block text-xs text-[#9CA3AF]">{l}</span>)}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-white mb-2">Mobile App</h4>
              <div className="space-y-1">
                <a href="https://play.google.com/store/apps/details?id=app.kstreak" target="_blank" rel="noopener noreferrer" className="block text-xs text-[#9CA3AF] hover:text-white transition-colors">Google Play Store</a>
                <span className="block text-xs text-[#9CA3AF]">App Store <span className="text-[#FF9500]">(Soon)</span></span>
                <span className="block text-xs text-[#9CA3AF]">Amazon Appstore <span className="text-[#FF9500]">(Soon)</span></span>
              </div>
            </div>
          </div>
          <div className="border-t border-[#1F1F1F] pt-4 flex flex-col md:flex-row justify-between items-center">
            <p className="text-xs text-[#9CA3AF]">&copy; 2026 KStreak. All rights reserved.</p>
            <div className="flex gap-4 mt-2 md:mt-0">
              {['Privacy Policy', 'Terms of Service', 'Security'].map(l => <span key={l} className="text-xs text-[#9CA3AF] hover:text-white cursor-pointer transition-colors">{l}</span>)}
            </div>
          </div>
        </div>
      </footer>

      {/* Book Demo / Enterprise Trial Modal */}
      {showBooking && (
        <div className="fixed inset-0 bg-[#0A0A0A]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowBooking(false); }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" data-testid="booking-modal">
            <h3 className="text-lg font-semibold text-[#0A0A0A] mb-1">Book a Demo or Enterprise Trial</h3>
            <p className="text-xs text-[#9CA3AF] mb-5">A solutions engineer will respond within 4 business hours.</p>
            <div className="space-y-3">
              <div><label className="text-xs font-medium text-[#4B5563] block mb-1">Full Name *</label><Input value={bookingForm.name} onChange={e => setBookingForm({...bookingForm, name: e.target.value})} placeholder="Jane Smith" data-testid="booking-name" /></div>
              <div><label className="text-xs font-medium text-[#4B5563] block mb-1">Work Email *</label><Input type="email" value={bookingForm.email} onChange={e => setBookingForm({...bookingForm, email: e.target.value})} placeholder="jane@company.com" data-testid="booking-email" /></div>
              <div><label className="text-xs font-medium text-[#4B5563] block mb-1">Company Name *</label><Input value={bookingForm.company} onChange={e => setBookingForm({...bookingForm, company: e.target.value})} placeholder="Acme Corp" /></div>
              <div><label className="text-xs font-medium text-[#4B5563] block mb-1">Company Size</label>
                <select className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" value={bookingForm.size} onChange={e => setBookingForm({...bookingForm, size: e.target.value})}>
                  <option value="">Select</option><option value="1-50">1-50</option><option value="51-250">51-250</option><option value="251-500">251-500</option><option value="500+">500+</option>
                </select>
              </div>
              <div><label className="text-xs font-medium text-[#4B5563] block mb-1">Primary Use Case</label><Input value={bookingForm.useCase} onChange={e => setBookingForm({...bookingForm, useCase: e.target.value})} placeholder='e.g., "Knowledge Transfer for Engineering Team"' /></div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button variant="outline" className="flex-1" onClick={() => setShowBooking(false)}>Cancel</Button>
              <Button className="flex-1 bg-[#002FA7] hover:bg-[#00227A]" onClick={() => { setShowBooking(false); alert('Thank you! A solutions engineer will contact you within 4 business hours.'); }} data-testid="booking-submit">
                <Mail className="w-4 h-4 mr-2" />Submit Request
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
